import CodeService from "@/src/api/CodeService.js";
import {
  terminalRunStatuses,
  terminalJobStatuses,
} from "@/src/constants/codebaseConstants.js";

export default {
  namespaced: true,

  state: {
    runId: null,
    run: null,
    jobs: [],
    stepsMap: {},
    pollTimer: null,
    isPolling: false,
  },

  mutations: {
    SET_RUN_ID(state, runId) {
      state.runId = runId;
    },
    SET_RUN(state, run) {
      state.run = run;
    },
    SET_JOBS(state, jobs) {
      state.jobs = jobs;
    },
    SET_STEPS(state, { jobId, steps }) {
      state.stepsMap = { ...state.stepsMap, [jobId]: steps };
    },
    SET_POLL_TIMER(state, timer) {
      state.pollTimer = timer;
    },
    SET_IS_POLLING(state, val) {
      state.isPolling = val;
    },
    RESET(state) {
      if (state.pollTimer) clearInterval(state.pollTimer);
      state.runId = null;
      state.run = null;
      state.jobs = [];
      state.stepsMap = {};
      state.pollTimer = null;
      state.isPolling = false;
    },
  },

  actions: {
    async fetchRun({ commit, state }) {
      if (!state.runId) return;
      try {
        const response = await CodeService.actions.runs.get(state.runId);
        if (response?.success) commit("SET_RUN", response.run || null);
      } catch {}
    },

    async fetchJobs({ commit, state }) {
      if (!state.runId) return;
      try {
        const response = await CodeService.actions.runs.getJobs(state.runId);
        if (response?.success) {
          const sorted = (response.items || []).sort((a, b) => {
            if (!a.started_at && !b.started_at) return 0;
            if (!a.started_at) return -1;
            if (!b.started_at) return 1;
            return new Date(a.started_at) - new Date(b.started_at);
          });
          commit("SET_JOBS", sorted);
        }
      } catch {}
    },

    async fetchSteps({ commit }, jobId) {
      try {
        const response = await CodeService.actions.jobs.getSteps(jobId);
        if (response?.success)
          commit("SET_STEPS", { jobId, steps: response.items || [] });
      } catch {}
    },

    async poll({ commit, dispatch, state }) {
      if (state.isPolling) return;
      commit("SET_IS_POLLING", true);
      try {
        await dispatch("fetchRun");
        await dispatch("fetchJobs");
        for (const job of state.jobs) {
          const alreadyFetched = !!state.stepsMap[job.id];
          const isTerminal = terminalJobStatuses.includes(job.status);
          if (!alreadyFetched || !isTerminal) {
            await dispatch("fetchSteps", job.id);
          }
        }
      } finally {
        commit("SET_IS_POLLING", false);
      }
    },

    startPolling({ commit, dispatch, state }, { runId, interval = 10000 }) {
      if (state.pollTimer) clearInterval(state.pollTimer);
      commit("SET_RUN_ID", runId);
      dispatch("poll");
      const timer = setInterval(() => {
        if (state.run && terminalRunStatuses.includes(state.run.status)) {
          clearInterval(timer);
          commit("SET_POLL_TIMER", null);
          return;
        }
        dispatch("poll");
      }, interval);
      commit("SET_POLL_TIMER", timer);
    },

    stopPolling({ commit, state }) {
      if (state.pollTimer) clearInterval(state.pollTimer);
      commit("SET_POLL_TIMER", null);
    },

    reset({ commit }) {
      commit("RESET");
    },

    clearRun({ commit }) {
      commit("RESET");
    },
  },

  getters: {
    run: (state) => state.run,
    jobs: (state) => state.jobs,
    stepsForJob: (state) => (jobId) => state.stepsMap[jobId] || [],
    isTerminal: (state) =>
      state.run ? terminalRunStatuses.includes(state.run.status) : false,
  },
};
